import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { isDespiaNative } from "@/lib/isDespiaNative";
import { camposDelAviso, rolDeAviso, rutaDeAviso, type RolDeAviso } from "@/lib/notificationRoutes";
import { instalarBuzonDeAvisos, recogerAvisoPendiente } from "@/lib/avisoPendiente";
import { mandarParteDeArranque } from "@/lib/diagnosticoDeArranque";

/**
 * Tocar un aviso en el móvil lleva a donde dice el aviso.
 *
 * TRES RONDAS DE PRUEBA CON EL USUARIO (02-09-2026), y cada una destapó una capa
 * distinta. Vale la pena dejarlas escritas porque la de abajo solo se veía
 * después de arreglar la de arriba:
 *
 *   1. NADIE LEÍA EL DESTINO. El push viajaba con la ruta bajo la llave `route`
 *      y el envoltorio nativo busca `data.path`. Tocar el aviso abría la app y
 *      ahí se quedaba.
 *   2. EL DESTINO VENÍA HECHO, Y VENÍA MAL. Con el punto 1 resuelto, el
 *      Planificador se abría pero la ficha de la tarea no. Lo que llegaba era un
 *      «/planner» pelado — el que escribe el disparador de la base de datos—
 *      sin el `?taskId=`. Ahora el destino se CALCULA aquí (ver `destinoDe`) en
 *      vez de creerse el que venga.
 *   3. EL AVISO SE PERDÍA ANTES DE EMPEZAR. Y ésta era la de verdad. Con la app
 *      ABIERTA todo lo anterior funcionaba; con la app CERRADA, no. Ese contraste
 *      es el que lo explica: si en caliente abre la ficha, el `data` se lee bien
 *      y la ruta se calcula bien — lo que fallaba era CUÁNDO.
 *      `window.onNotificationEvent` es de UN SOLO DISPARO y se instalaba en este
 *      `useEffect`, o sea al final de «bajar 1,5 MB → interpretarlos → montar
 *      React». Con la app cerrada y datos móviles, el envoltorio llamaba antes
 *      de que la propiedad existiera y el aviso se perdía sin ruido. El usuario
 *      aterrizaba en la pantalla que el WebView tenía guardada de la sesión
 *      anterior — que esos días era el Planificador, y por eso parecía que el
 *      enlace medio funcionaba.
 *      Lo arregla `src/lib/avisoPendiente.ts`: un buzón puesto desde el `<head>`
 *      del `index.html`, mucho antes de que exista React. Aquí solo se recoge.
 *
 * NO ERA LA PANTALLA DE BIENVENIDA, aunque lo pareciera y aunque el usuario
 * apuntara ahí tres veces. `SplashGate` pinta un div por encima y ya está: no
 * toca el router, no desmonta nada, y `children` sigue montado debajo mientras
 * se ve. Lo que hacía era TAPAR el fallo — para cuando se pulsa «Empezar», lo
 * que tenía que pasar ya pasó o ya se perdió.
 *
 * ENTONCES, ¿PARA QUÉ ESTE COMPONENTE? Para los dos casos en los que el
 * automatismo del envoltorio (`pushState` + `popstate` con `data.path`) no
 * basta: cuando el aviso llega antes de que haya sesión, y cuando ese `path` no
 * viene o viene sin el id. Es un cinturón, y últimamente el cinturón es lo único
 * que sujeta.
 *
 * EL ROL IMPORTA: un trabajador no tiene Planificador. Por eso se guarda el
 * aviso EN CRUDO y se resuelve al final, cuando ya se sabe quién es.
 */
/* `window.onNotificationEvent` se declara en `avisoPendiente`, que es quien la
   instala antes de que exista este componente. */

/** Solo rutas internas: esto viene de la red y una URL de fuera sacaría al
    usuario de la aplicación llevándose la sesión abierta. */
function rutaInterna(candidato: unknown): string | null {
  if (typeof candidato !== "string" || !candidato) return null;
  if (!candidato.startsWith("/") || candidato.startsWith("//")) return null;
  return candidato;
}

/**
 * El destino que venía en el aviso, mire por donde mire.
 *
 * EL DESTINO SE CALCULA AQUÍ, NO SE CREE EL QUE VENGA HECHO (02-09-2026).
 * Antes esto leía `data.path ?? data.route` y navegaba a lo que dijera. Con el
 * arreglo del calendario puesto, el usuario probó otra vez: la app abría el
 * Planificador pero NO la ficha de la tarea. En la base de datos el aviso
 * llevaba su `task_id` — el id se estaba perdiendo entre el servidor y el
 * teléfono, y solo hay dos sitios donde puede pasar eso:
 *
 *   · la función edge desplegada es anterior al cambio que manda `path`, con lo
 *     que solo llega el `route: "/planner"` pelado que escribe el disparador de
 *     la base de datos; o
 *   · el envoltorio entrega el `data` de OneSignal anidado en algún sitio que no
 *     estábamos mirando.
 *
 * Las dos se arreglan igual y sin tener que averiguar cuál es: `camposDelAviso`
 * busca las claves por todo el objeto, y luego el destino se CALCULA con la
 * misma `rutaDeAviso` que usa la campana de dentro de la app. Mientras el aviso
 * traiga su `type` y su `task_id` —y los trae, están en la fila— la ficha se
 * abre, dé igual lo que el servidor hubiera precalculado.
 *
 * Además así el ROL lo decide quien lo sabe. El servidor tiene que deducir si
 * quien recibe es trabajador; aquí no hay nada que deducir.
 *
 * El `path`/`route` que venga hecho sigue valiendo de reserva, para un aviso a
 * medida de un tipo que estas reglas no conozcan.
 */
export function destinoDe(payload: unknown, quien: boolean | RolDeAviso = false): string | null {
  /* `true`/`false` es la firma de antes (¿es trabajador?); ahora llega el rol,
     porque el perito también tiene pantallas propias (26-09-2026). */
  const rol: RolDeAviso = typeof quien === "boolean" ? (quien ? "worker" : "other") : quien;
  const campos = camposDelAviso(payload);
  /* De los dos comodines gana `path`: es el que manda la función edge de ahora,
     mientras que `route` puede ser el «/planner» pelado que escribió el
     disparador hace meses. `rutaDeAviso` solo entiende `route`, así que se le
     entrega ya resuelto — y filtrado, para que un destino de fuera no entre por
     la puerta del comodín. */
  const aMano = rutaInterna(campos.path) ?? rutaInterna(campos.route);
  const calculado = rutaDeAviso({
    type: typeof campos.type === "string" ? campos.type : "",
    data: { ...campos, route: aMano ?? undefined },
    rol,
  });
  return rutaInterna(calculado) ?? aMano;
}

export function DespiaNotificationRouter() {
  const navigate = useNavigate();
  const { user, loading, role } = useAuth();
  const rolAviso = rolDeAviso(role);
  /* EL AVISO EN CRUDO, no el destino ya resuelto (02-09-2026). Guardar la ruta
     hecha tenía una trampa en el arranque en frío: el aviso llega antes de que
     se sepa el rol, y el destino DEPENDE del rol — un trabajador no tiene
     Planificador, su tarea vive en `/worker/tasks/:id`. Resolviendo entonces, a
     un trabajador se le mandaba a una pantalla que no puede abrir. Guardando el
     aviso y resolviendo al final, el rol ya se sabe. */
  const pendiente = useRef<unknown>(null);

  useEffect(() => {
    if (!isDespiaNative()) return;

    /* LO QUE LLEGÓ ANTES DE QUE EXISTIÉRAMOS. Con la app cerrada, el envoltorio
       entrega el aviso en cuanto la página carga, y eso puede ser mucho antes de
       que React monte: el buzón de `index.html` lo tiene guardado desde
       entonces. Sin esto, ese aviso se perdía y el arranque en frío no llevaba a
       ninguna parte — mientras que en caliente funcionaba. */
    const delBuzon = recogerAvisoPendiente();
    if (delBuzon !== null && pendiente.current == null) pendiente.current = delBuzon;

    window.onNotificationEvent = (payload: unknown) => {
      if (!destinoDe(payload, rolAviso)) return;
      pendiente.current = payload;
      /* Con la aplicación YA EN PIE se va directo; si no, lo recoge el efecto de
         abajo en cuanto lo esté.

         Y «en pie» es `!loading`, no `user?.id` a secas (02-09-2026). Había ahí
         una ventana de un segundo largo en el arranque en frío: `getSession()`
         devuelve el usuario mucho antes de que termine de hidratarse el perfil y
         la empresa, así que con solo mirar el usuario se navegaba a media
         arranque, con `company` todavía en null y el árbol de rutas sin montar.
         Esperar a que `loading` caiga es lo que decía este componente que hacía
         desde el principio. */
      if (!loading && user?.id) {
        const destino = destinoDe(pendiente.current, rolAviso);
        pendiente.current = null;
        if (destino) navigate(destino);
      }
    };

    return () => {
      /* Se DEVUELVE el buzón, no se borra. Con `delete` la propiedad volvía a no
         existir y el siguiente aviso que llegara se perdería igual. */
      instalarBuzonDeAvisos();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, user?.id, loading, rolAviso]);

  /* El arranque en frío pasa por aquí: el aviso llegó sin sesión y se aplica en
     cuanto la hay. Si el usuario no llega a identificarse, no se navega nunca,
     que es lo correcto. */
  useEffect(() => {
    if (loading || !user?.id) return;
    const aviso = pendiente.current;
    const destino = aviso != null ? destinoDe(aviso, rolAviso) : null;
    pendiente.current = null;

    /* LA CAJA NEGRA, una vez por arranque. Va aquí y no antes porque este es el
       momento en que ya se sabe todo: si llegó aviso, qué traía, y a dónde se
       va. Manda `severity: "info"`, que inserta la fila y NO manda correo.
       Temporal: fuera en cuanto el arranque en frío funcione. */
    mandarParteDeArranque({
      destino,
      aviso,
      urlAhora: typeof window !== "undefined"
        ? window.location.pathname + window.location.search
        : "",
    });

    if (destino) navigate(destino);
  }, [loading, user?.id, navigate, rolAviso]);

  return null;
}
